import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { DASHBOARD_TABS } from '../constants';
import { useDashboardData } from './DashboardDataContext';

const DashboardUIContext = createContext(null);

export const DashboardUIProvider = ({ children }) => {
  const location = useLocation();
  const { fetchDashboards, fetchTemplates } = useDashboardData();

  const [state, setState] = useState({
    activeTab: DASHBOARD_TABS.CREATED,
    filters: { search: '', state: '', sectionId: '' },
    templateFilters: { search: '', sectionId: '', categoryId: '' },
  });

  const setFilters = useCallback((newFilters) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, ...newFilters },
    }));
  }, []);

  const setTemplateFilters = useCallback((newFilters) => {
    setState((prev) => ({
      ...prev,
      templateFilters: { ...prev.templateFilters, ...newFilters },
    }));
  }, []);

  const setActiveTab = useCallback((tab) => {
    setState((prev) => ({ ...prev, activeTab: tab }));
  }, []);

  useEffect(() => {
    const tab = location.pathname.includes('/templates')
      ? DASHBOARD_TABS.TEMPLATES
      : DASHBOARD_TABS.CREATED;
    setState((prev) => (prev.activeTab !== tab ? { ...prev, activeTab: tab } : prev));
  }, [location.pathname]);

  useEffect(() => {
    const isListRoute =
      !location.pathname.includes('/new') &&
      !location.pathname.includes('/edit') &&
      !location.pathname.includes('/templates');

    if (state.activeTab === DASHBOARD_TABS.CREATED && isListRoute) {
      fetchDashboards(state.filters);
    }
  }, [state.activeTab, state.filters, fetchDashboards, location.pathname]);

  useEffect(() => {
    if (state.activeTab === DASHBOARD_TABS.TEMPLATES) {
      fetchTemplates(state.templateFilters);
    }
  }, [state.activeTab, state.templateFilters, fetchTemplates]);

  const value = useMemo(
    () => ({
      activeTab: state.activeTab,
      filters: state.filters,
      templateFilters: state.templateFilters,
      setFilters,
      setTemplateFilters,
      setActiveTab,
    }),
    [
      state.activeTab,
      state.filters,
      state.templateFilters,
      setFilters,
      setTemplateFilters,
      setActiveTab,
    ]
  );

  return (
    <DashboardUIContext.Provider value={value}>
      {children}
    </DashboardUIContext.Provider>
  );
};

export const useDashboardUI = () => {
  const context = useContext(DashboardUIContext);

  if (!context) {
    throw new Error(
      'useDashboardUI must be used within DashboardUIProvider'
    );
  }

  return context;
};
